import React, { useState } from 'react';
import { freeZoneList } from '../data/freeZones';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram, Youtube, Twitter, Send, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { trackNewsletter } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (email) {
      try {
        const response = await axios.post(`${API}/newsletter`, { email });
        if (response.data.success) {
          toast.success("Subscribed!", {
            description: response.data.message,
          });
          trackNewsletter({ email });
          setEmail('');
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Newsletter subscription error:', error);
        }
        toast.error("Subscription error", {
          description: "Please try again later.",
        });
      }
    }
  };

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Industries', path: '/industries' },
    { name: 'Why Choose Us', path: '/why-choose-us' },
    { name: 'Team', path: '/team' },
    { name: 'Blog', path: '/blog' },
    { name: 'WPAA Academy', path: '/knowledge-academy' },
    { name: 'Contact', path: '/contact' }
  ];

  const services = [
    { name: 'Audit Services in UAE', path: '/audit-services-uae' },
    { name: 'Corporate Tax Services', path: '/corporate-tax-uae' },
    { name: 'VAT Services', path: '/vat-services-uae' },
    { name: 'E-Invoicing', path: '/e-invoicing-uae' },
    { name: 'goAML Compliance', path: '/services/goaml-compliance' },
    { name: 'Valuation Services', path: '/services/valuation' },
    { name: 'Accounting Services', path: '/accounting-services-uae' },
    { name: 'Business Advisory', path: '/business-advisory-uae' },
    { name: 'SME Business Package', path: '/sme-business-services-uae' },
    { name: 'Company Formation', path: '/company-formation-uae' },
    { name: 'UAE Free Zone Services', path: '/free-zone-services-uae' },
    { name: 'Hamriyah Free Zone', path: '/hamriyah-free-zone-services' },
    { name: 'SAIF Zone Services', path: '/saif-zone-services' }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-300">
      {/* Newsletter Section */}
      <div className="border-b border-gray-700">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-white mb-3">Stay Updated</h3>
            <p className="text-gray-400 mb-6">
              Subscribe to our newsletter for the latest insights on audit, tax, and advisory services in the UAE.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-teal-500"
              />
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap">
                <Send size={16} className="mr-2" />
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <span className="text-2xl font-bold text-white">WHITE PAPER</span>
              <p className="text-teal-400 text-xs tracking-wider">AUDIT | TAX | ADVISORY</p>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Your trusted partner in audit, tax, and advisory services in the UAE. Delivering excellence and integrity, backed by 18+ years of combined industry expertise.
            </p>
            <div className="flex space-x-3">
              <a href="https://www.linkedin.com/company/wpaauae" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors">
                <Linkedin size={18} />
              </a>
              <a href="https://www.youtube.com/@wpaauae" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors">
                <Youtube size={18} />
              </a>
              <a href="https://www.facebook.com/wpaauae" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors">
                <Facebook size={18} />
              </a>
              <a href="https://www.instagram.com/whitepaperaudit" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors">
                <Instagram size={18} />
              </a>
              <a href="https://x.com/wpaauae" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors">
                <Twitter size={18} />
              </a>
              <a href="https://www.tiktok.com/@wpaauae" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.62a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.05Z"/>
                </svg>
              </a>
              <a href="https://www.pinterest.com/wpaauae" target="_blank" rel="noopener noreferrer" aria-label="Pinterest" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.93-.2-2.37.04-3.39.22-.92 1.4-5.85 1.4-5.85s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.02 0 1.52.77 1.52 1.69 0 1.03-.66 2.57-1 4-.28 1.2.6 2.18 1.79 2.18 2.15 0 3.8-2.27 3.8-5.54 0-2.9-2.08-4.92-5.05-4.92-3.44 0-5.46 2.58-5.46 5.25 0 1.04.4 2.16.9 2.77.1.12.11.22.08.34l-.34 1.36c-.05.22-.18.27-.41.16-1.5-.7-2.44-2.89-2.44-4.65 0-3.78 2.75-7.26 7.93-7.26 4.16 0 7.4 2.97 7.4 6.93 0 4.14-2.61 7.47-6.23 7.47-1.21 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1 0 12 0Z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-sm hover:text-teal-400 transition-colors inline-block hover:translate-x-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">Our Services</h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.path}>
                  <Link 
                    to={service.path} 
                    className="text-sm hover:text-teal-400 transition-colors inline-block hover:translate-x-1"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-teal-500 mt-1 flex-shrink-0" />
                <span className="text-sm">109, Midaffa Bldg., Musab Bin Omair St., Al Mahatta, Sharjah, UAE</span>
              </li>
              <li className="flex items-start space-x-3">
                <Phone size={18} className="text-teal-500 mt-1 flex-shrink-0" />
                <div>
                  <a href="tel:+971501219341" className="text-sm hover:text-teal-400 transition-colors block">
                    +971 50 121 9341
                  </a>
                  <a href="tel:+97167447940" className="text-sm hover:text-teal-400 transition-colors block" data-testid="footer-office-phone">
                    Office: +971 6 744 7940
                  </a>
                  <span className="text-xs text-gray-500">WhatsApp Available</span>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail size={18} className="text-teal-500 mt-1 flex-shrink-0" />
                <a href="mailto:info@wpaauae.com" className="text-sm hover:text-teal-400 transition-colors">
                  info@wpaauae.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Free zone pages: sitewide crawl path */}
        <div className="border-t border-gray-700 pt-6 mt-8" data-testid="footer-free-zones">
          <h4 className="text-white font-semibold text-sm mb-3">Free Zones We Support</h4>
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {freeZoneList.map((z) => (
              <li key={z.slug}>
                <Link to={z.slug} className="text-sm hover:text-teal-400 transition-colors">{z.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400 flex items-center gap-3 flex-wrap justify-center md:justify-start">
              <span>
                © {new Date().getFullYear()} White Paper Audit | Tax | Advisory. All rights reserved.
              </span>
              <span className="text-gray-600 hidden md:inline">·</span>
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-1 text-gray-500 hover:text-teal-400 transition-colors text-xs"
                data-testid="footer-staff-login"
                title="WPAA staff sign in"
              >
                <Lock size={11} />
                Staff Login
              </Link>
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-teal-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-sm text-gray-400 hover:text-teal-400 transition-colors">
                Terms of Service
              </Link>
              <Link to="/cookie-policy" className="text-sm text-gray-400 hover:text-teal-400 transition-colors">
                Cookie Policy
              </Link>
              <Link to="/fraud-alert" className="text-sm text-gray-400 hover:text-red-400 transition-colors">
                Fraud Alert
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
